import { t as createQuizTracker } from "@theme/quiz-analytics";
//#region frontend/islands/dog-food-calculator.js
/**
* Standalone dog food calculator.
*
* Collects the same answers as the quiz (name, age, weight, shape, breed, condition) on a single
* page, then turns them into a feeding guide and a transition schedule.
*
* All portion numbers come from the same /api/meals endpoint the quiz uses, so the guide shown here
* always agrees with the plan the shopper is sold on the PDP. The API only accepts weight and
* condition — age and shape deliberately do NOT change the pack count. Age sets meals per day and
* shape drives written advice, which keeps the headline number identical to the plan they land on.
*/
var EIGHTHS = [
	"",
	"⅛",
	"¼",
	"⅜",
	"½",
	"⅝",
	"¾",
	"⅞"
];
/** Renders a pack count as a mixed fraction, e.g. 1.71 -> "1¾", 0.43 -> "⅜". */
function formatPacks(value) {
	if (!Number.isFinite(value) || value <= 0) return "0";
	const eighths = Math.max(1, Math.round(value * 8));
	const whole = Math.floor(eighths / 8);
	const remainder = eighths % 8;
	if (!remainder) return String(whole);
	return whole ? `${whole}${EIGHTHS[remainder]}` : EIGHTHS[remainder];
}
/**
* "pack" / "packs", agreeing with the rounded display value rather than the raw float. Fractions of
* a single pack stay singular — "½ pack", not "½ packs".
*/
function packNoun(value) {
	return Math.round(value * 8) <= 8 ? "pack" : "packs";
}
var TRANSITION_PHASES = [
	{
		days: "Days 1–3",
		share: .25,
		note: "Start slow. Watch their stool and appetite."
	},
	{
		days: "Days 4–6",
		share: .5,
		note: "Half and half. Most dogs settle in here."
	},
	{
		days: "Days 7–9",
		share: .75,
		note: "Mostly Happy Howl now."
	},
	{
		days: "Day 10+",
		share: 1,
		note: "Fully switched. This is their daily portion."
	}
];
var SLOW_TRANSITION_CONDITIONS = [
	"GI",
	"VOMITING",
	"PANCREATITIS",
	"KIDNEY"
];
var DogFoodCalculator = class extends window.HTMLElement {
	connectedCallback() {
		this.productHandle = this.dataset.productHandle;
		this.apiUrl = this.dataset.apiUrl;
		this.form = this.querySelector("[data-calc-form]");
		this.results = this.querySelector("[data-calc-results]");
		this.loading = this.querySelector("[data-calc-loading]");
		this.error = this.querySelector("[data-calc-error]");
		this.submitButton = this.querySelector("[data-calc-submit]");
		this.mealOptions = {};
		this.selectedPortion = "full";
		this.variantUnits = this._readJSON("[data-calc-variant-units]") ?? {};
		this.addEventListener("click", this._onClick);
		this.addEventListener("input", this._onInput);
		this.form.addEventListener("submit", this._onSubmit);
		this._syncSubmitState();
		const deepLinked = this._applyUrlParams();
		this.analytics = createQuizTracker("calculator", { entryMode: deepLinked ? "deep_link" : "form" });
		this.analytics.start();
		if (deepLinked) {
			this.form.hidden = true;
			if (this.loading) this.loading.hidden = false;
			this._buildGuide();
		}
	}
	disconnectedCallback() {
		this.removeEventListener("click", this._onClick);
		this.removeEventListener("input", this._onInput);
		this.form?.removeEventListener("submit", this._onSubmit);
	}
	_readJSON(selector) {
		const el = this.querySelector(selector);
		if (!el) return null;
		try {
			return JSON.parse(el.textContent);
		} catch {
			return null;
		}
	}
	/**
	* Fills the form from the query string, e.g. ?name=annie&weight=34&condition=NONE.
	*
	* Accepts every answer the form collects — name, weight, age, shape, breed, condition — plus
	* `meal` to preselect a portion, which is the same vocabulary the results CTA emits when it hands
	* off to the PDP, so a link copied out of one page can be pasted back into this one.
	*
	* Returns whether there is enough to build a guide without the shopper touching anything. Only
	* name and weight are required, matching _syncSubmitState; anything unrecognised is dropped
	* rather than forwarded to the API.
	*/
	_applyUrlParams() {
		let params;
		try {
			params = new URLSearchParams(window.location.search);
		} catch {
			return false;
		}
		if (!params.has("name") && !params.has("weight")) return false;
		const name = this._titleCase((params.get("name") ?? "").trim().slice(0, 40));
		if (name) this._setInputValue("calc-dog-name", name);
		const weight = (params.get("weight") ?? "").replace(/\D/g, "").slice(0, 3);
		if (weight) this._setInputValue("calc-dog-weight", weight);
		const selectOption = (name, value) => {
			if (!value) return;
			const option = this.querySelector(`[data-calc-option][data-name="${name}"][data-value="${value}"]`);
			if (option) this._selectOption(option);
		};
		const lower = (key) => (params.get(key) ?? "").trim().toLowerCase();
		selectOption("calc-dog-age", lower("age"));
		selectOption("calc-dog-shape", lower("shape"));
		selectOption("calc-dog-condition", (params.get("condition") ?? "").trim().toUpperCase());
		this._applyBreedParam(params.get("breed"));
		const meal = lower("meal");
		if ([
			"full",
			"half",
			"quarter"
		].includes(meal)) this.selectedPortion = meal;
		this._syncSubmitState();
		return Boolean(this.submitButton) && !this.submitButton.disabled;
	}
	/**
	* Breed is passed through to the PDP rather than used in the calculation, so the hidden input is
	* what matters; the search box and cards are updated only to keep the revealed form honest.
	*/
	_applyBreedParam(value) {
		const breed = (value ?? "").trim();
		if (!breed) return;
		const hidden = this.querySelector("#calc-dog-breed");
		if (hidden) hidden.value = breed;
		const match = this.querySelector(`.breed-search__item[data-value="${CSS.escape(breed)}"], breed-search .radio-card[data-value="${CSS.escape(breed)}"]`);
		if (!match) return;
		match.classList.add("is-selected");
		match.setAttribute("aria-selected", "true");
		const search = this.querySelector(".breed-search__input");
		if (search && match.dataset.label) search.value = match.dataset.label;
	}
	_setInputValue(id, value) {
		const input = this.querySelector(`#${id}`);
		if (input) input.value = value;
	}
	/**
	* "annie" -> "Annie". URLs are typed in lower case far more often than a name box is, and the
	* name is dropped straight into sentences ("Annie is still growing"). Anything already carrying a
	* capital is left alone, so "McFluffy" survives.
	*/
	_titleCase(value) {
		if (!value || value !== value.toLowerCase()) return value;
		return value.replace(/(^|[\s'-])([a-z])/g, (_, lead, char) => lead + char.toUpperCase());
	}
	_onClick = (e) => {
		const option = e.target.closest("[data-calc-option]");
		if (option) {
			this._selectOption(option);
			return;
		}
		const portion = e.target.closest("[data-calc-portion]");
		if (portion) {
			this._selectPortion(portion.dataset.calcPortion);
			return;
		}
		if (e.target.closest("[data-calc-restart]")) this._restart();
	};
	_onInput = (e) => {
		const input = e.target.closest("input");
		if (!input) return;
		if (input.inputMode === "numeric") {
			input.value = input.value.replace(/\D/g, "");
			if (input.value !== "" && Number(input.value) > 999) input.value = "999";
		}
		this._syncSubmitState();
	};
	_selectOption(option) {
		const name = option.dataset.name;
		this.querySelectorAll(`[data-calc-option][data-name="${name}"]`).forEach((el) => {
			el.classList.remove("is-selected");
			el.setAttribute("aria-checked", "false");
		});
		option.classList.add("is-selected");
		option.setAttribute("aria-checked", "true");
		const hidden = this.querySelector(`#${name}`);
		if (hidden) hidden.value = option.dataset.value;
		const description = this.querySelector(`[data-calc-option-description="${name}"]`);
		if (description && option.dataset.description) description.textContent = option.dataset.description;
		this._syncSubmitState();
	}
	/** The plan needs a name and a usable weight; everything else is optional. */
	_answers() {
		const value = (id) => this.querySelector(`#${id}`)?.value.trim() ?? "";
		return {
			name: value("calc-dog-name"),
			age: value("calc-dog-age"),
			weight: value("calc-dog-weight"),
			shape: value("calc-dog-shape"),
			breed: value("calc-dog-breed"),
			condition: value("calc-dog-condition") || "NONE"
		};
	}
	_syncSubmitState() {
		const { name, weight } = this._answers();
		const pounds = Number(weight);
		const valid = name !== "" && Number.isFinite(pounds) && pounds > 0;
		if (this.submitButton) this.submitButton.disabled = !valid;
	}
	_onSubmit = (e) => {
		e.preventDefault();
		this._buildGuide();
	};
	/**
	* Fetches the plan and shows the guide. Shared by the form and by the deep link, which reaches it
	* with the form already hidden — hence `revealFormOnError`: a shopper who never saw the questions
	* needs them back to fix whatever the link got wrong.
	*/
	async _buildGuide() {
		const deepLinked = this.form.hidden;
		const answers = this._answers();
		const pounds = Number(answers.weight);
		if (!Number.isFinite(pounds) || pounds <= 0) {
			this._failed("Enter your dog’s weight in pounds to build their guide.", deepLinked);
			return;
		}
		this._hideError();
		this._setLoading(true);
		try {
			const params = new URLSearchParams({
				weight: answers.weight,
				condition: answers.condition
			});
			const response = await fetch(`${this.apiUrl}?${params}`);
			const data = await response.json();
			if (!response.ok || data.error || !data.full) {
				this._failed(pounds > 100 ? `We build plans up to 100 lbs in the calculator. Get in touch and we’ll size a plan for ${answers.name} by hand.` : `We couldn’t build a plan for ${answers.name} just now. Please try again in a moment.`, deepLinked);
				return;
			}
			this.mealOptions = data;
			this.answers = answers;
			const portion = this.mealOptions[this.selectedPortion] ? this.selectedPortion : "full";
			this._selectPortion(portion, { silent: true });
			this._render({ focus: !deepLinked });
			const chosen = this.mealOptions[this.selectedPortion];
			this.analytics.complete({
				weight: answers.weight,
				mealSize: this.selectedPortion,
				conditionCode: answers.condition,
				variantId: chosen?.variant,
				sellingPlanId: chosen?.sellingPlan
			});
		} catch {
			this.analytics.error("meals_fetch_failed");
			this._failed("We couldn’t reach our plan builder. Check your connection and try again.", deepLinked);
		} finally {
			this._setLoading(false);
			if (this.loading) this.loading.hidden = true;
		}
	}
	/** Shows the error, putting the form back first when the shopper arrived past it via a link. */
	_failed(message, deepLinked) {
		if (deepLinked) {
			this.form.hidden = false;
			if (this.loading) this.loading.hidden = true;
		}
		this._showError(message);
	}
	_setLoading(loading) {
		if (!this.submitButton) return;
		this.submitButton.disabled = loading;
		this.submitButton.textContent = loading ? "Building guide…" : this.submitButton.dataset.label;
		if (!loading) this._syncSubmitState();
	}
	_showError(message) {
		if (!this.error) return;
		this.error.textContent = message;
		this.error.hidden = false;
	}
	_hideError() {
		if (!this.error) return;
		this.error.hidden = true;
		this.error.textContent = "";
	}
	/** Packs per day for a portion size, derived from pack count ÷ days the plan covers. */
	_packsPerDay(mealKey) {
		const meal = this.mealOptions?.[mealKey];
		if (!meal || !meal.daysOfFood) return 0;
		return (Number(this.variantUnits[String(meal.variant)]) || Number(/(\d+)-Pack/.exec(meal.variantTitle || "")?.[1]) || 0) / meal.daysOfFood;
	}
	/** Puppies do better on three smaller meals; everyone else gets the standard two. */
	_mealsPerDay() {
		return this.answers.age === "puppy" ? 3 : 2;
	}
	_transitionDays() {
		return SLOW_TRANSITION_CONDITIONS.includes(this.answers.condition) ? 14 : 10;
	}
	_selectPortion(mealKey, { silent = false } = {}) {
		this.selectedPortion = mealKey;
		this.querySelectorAll("[data-calc-portion]").forEach((el) => {
			const active = el.dataset.calcPortion === mealKey;
			el.classList.toggle("is-selected", active);
			el.setAttribute("aria-checked", String(active));
		});
		if (!silent) this._render();
	}
	_render({ focus = true } = {}) {
		const { name } = this.answers;
		const packsPerDay = this._packsPerDay(this.selectedPortion);
		const mealsPerDay = this._mealsPerDay();
		const perMeal = packsPerDay / mealsPerDay;
		this.querySelectorAll("[data-calc-name]").forEach((el) => {
			el.textContent = name;
		});
		this._set("[data-calc-daily-packs]", formatPacks(packsPerDay));
		this._set("[data-calc-daily-unit]", packNoun(packsPerDay));
		this._set("[data-calc-meals-per-day]", String(mealsPerDay));
		this._set("[data-calc-per-meal]", formatPacks(perMeal));
		this._set("[data-calc-per-meal-unit]", packNoun(perMeal));
		this._set("[data-calc-weekly]", formatPacks(packsPerDay * 7));
		this._set("[data-calc-weekly-unit]", packNoun(packsPerDay * 7));
		this._set("[data-calc-transition-days]", String(this._transitionDays()));
		this._renderPortions();
		this._renderTransition(packsPerDay, mealsPerDay);
		this._renderNotes();
		this._renderCta();
		this.form.hidden = true;
		this.results.hidden = false;
		if (focus) {
			this.results.focus();
			this.results.scrollIntoView({
				behavior: "smooth",
				block: "start"
			});
		}
	}
	_set(selector, text) {
		this.querySelectorAll(selector).forEach((el) => {
			el.textContent = text;
		});
	}
	_renderPortions() {
		let previousPacks = Infinity;
		[
			"full",
			"half",
			"quarter"
		].forEach((key) => {
			const meal = this.mealOptions?.[key];
			const card = this.querySelector(`[data-calc-portion="${key}"]`);
			if (!card) return;
			const packs = meal ? Math.round(this._packsPerDay(key) * 8) / 8 : 0;
			const usable = Boolean(meal) && packs > 0 && packs < previousPacks;
			card.hidden = !usable;
			if (!usable) return;
			previousPacks = packs;
			const amount = card.querySelector("[data-calc-portion-amount]");
			const price = card.querySelector("[data-calc-portion-price]");
			const priceRow = card.querySelector("[data-calc-portion-price-row]");
			if (amount) amount.textContent = `${formatPacks(packs)} ${packNoun(packs)}/day`;
			if (priceRow) priceRow.hidden = meal.costPerDay == null;
			if (price && meal.costPerDay != null) price.textContent = `$${Number(meal.costPerDay).toFixed(2)}`;
		});
	}
	_renderTransition(packsPerDay, mealsPerDay) {
		const list = this.querySelector("[data-calc-transition]");
		const template = this.querySelector("[data-calc-transition-row]");
		if (!list || !template) return;
		list.textContent = "";
		const labels = this._transitionDays() === 14 ? [
			"Days 1–4",
			"Days 5–8",
			"Days 9–12",
			"Day 13+"
		] : TRANSITION_PHASES.map((phase) => phase.days);
		TRANSITION_PHASES.forEach((phase, index) => {
			const row = template.content.firstElementChild.cloneNode(true);
			const packs = packsPerDay * phase.share;
			const perMeal = packs / mealsPerDay;
			const set = (selector, text) => {
				const el = row.querySelector(selector);
				if (el) el.textContent = text;
			};
			const percent = Math.round(phase.share * 100);
			row.classList.toggle("is-complete", phase.share === 1);
			set("[data-row-days]", labels[index]);
			set("[data-row-mix]", `${percent}%`);
			set("[data-row-amount]", `${formatPacks(packs)} ${packNoun(packs)}/day · ${formatPacks(perMeal)} per meal`);
			set("[data-row-remainder]", phase.share === 1 ? "Their old food is done." : `${Math.round((1 - phase.share) * 100)}% their current food`);
			set("[data-row-note]", phase.note);
			const fill = row.querySelector("[data-row-fill]");
			if (fill) fill.style.width = `${percent}%`;
			const progress = row.querySelector("[data-row-progress]");
			if (progress) {
				progress.setAttribute("aria-valuenow", String(percent));
				progress.setAttribute("aria-valuetext", `${percent}% Happy Howl`);
			}
			list.appendChild(row);
		});
	}
	/**
	* Age, shape and health answers don't move the pack count — they show up here as advice, so the
	* headline number stays identical to the plan on the product page.
	*/
	_renderNotes() {
		const list = this.querySelector("[data-calc-notes]");
		const template = this.querySelector("[data-calc-note-row]");
		if (!list || !template) return;
		const { name, age, shape, condition } = this.answers;
		const notes = [];
		if (age === "puppy") notes.push(`${name} is still growing, so split the day's food across 3 smaller meals instead of 2. Puppies also gain weight fast — recheck this portion every few weeks.`);
		else if (age === "senior") notes.push(`Senior dogs usually burn less than they used to. If ${name} starts filling out, drop to the next portion down rather than trimming each bowl by eye.`);
		if (shape === "underweight") notes.push(`Because ${name} is on the lean side, stay at the top of this range and weigh them again in 3 weeks. If they haven't gained, move up a portion size.`);
		else if (shape === "overweight") notes.push(`${name} is carrying a little extra, so hold this portion steady rather than free-feeding, and recheck their shape in 3 weeks. Treats count toward the daily total.`);
		else if (shape === "ideal") notes.push(`${name} is in good shape — keep this portion consistent and re-run the calculator if their weight changes by more than a few pounds.`);
		if (SLOW_TRANSITION_CONDITIONS.includes(condition)) notes.push(`Given what you told us about ${name}'s digestion, take the switch slowly — 14 days instead of 10, and hold a phase longer if their stool changes.`);
		notes.push("These are starting guidelines. Every dog is different — adjust based on body condition and energy levels, and check with your vet if anything looks off.");
		list.textContent = "";
		notes.forEach((note) => {
			const row = template.content.firstElementChild.cloneNode(true);
			const text = row.querySelector("[data-note-text]");
			if (text) text.textContent = note;
			list.appendChild(row);
		});
	}
	/**
	* Hands off to the PDP with the same params and sessionStorage keys the quiz sets, so a shopper
	* arriving from the calculator sees the identical customized plan view.
	*/
	_renderCta() {
		const cta = this.querySelector("[data-calc-cta]");
		if (!cta || !this.productHandle) return;
		const meal = this.mealOptions?.[this.selectedPortion];
		const { name, weight, condition, breed } = this.answers;
		const params = new URLSearchParams({
			name,
			weight,
			condition,
			meal: this.selectedPortion,
			from_quiz: "1",
			quiz_source: "calculator",
			...breed && breed !== "skip" && { breed },
			...meal && {
				variant: meal.variant,
				sellingPlan: meal.sellingPlan
			}
		});
		cta.href = `/products/${this.productHandle}?${params}`;
		cta.onclick = () => {
			try {
				sessionStorage.setItem("from_quiz", "1");
				sessionStorage.setItem("quiz_meal_options", JSON.stringify(this.mealOptions));
			} catch {}
		};
	}
	_restart() {
		this.results.hidden = true;
		this.form.hidden = false;
		this._hideError();
		this.form.scrollIntoView({
			behavior: "smooth",
			block: "start"
		});
		this.querySelector("#calc-dog-name")?.focus();
	}
};
window.customElements.define("dog-food-calculator", DogFoodCalculator);
//#endregion
